import _extends from "@babel/runtime/helpers/extends";
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Dropdown from './Dropdown';
import FocusService from '@pattern-library/utils/dist/FocusService';
const propTypes = {
  /**
   * Optional: dropdown alignment
   */
  align: PropTypes.oneOf(['left', 'right']),
  /**
   * Content displayed in the trigger button
   */
  buttonText: PropTypes.string.isRequired,
  /**
   * Optional: button classes to override styling
   */
  buttonStyle: PropTypes.string,
  /**
   * Optional: Additional class names
   */
  className: PropTypes.string,
  /**
   * unique ID
   */
  id: PropTypes.string.isRequired,
  /**
   * Optional: label to display above the button
   */
  label: PropTypes.string,
  /**
   * higher-order component to wrap each link element
   */
  linkHOC: PropTypes.func,
  /**
   * Array of menu items
   */
  menuItems: PropTypes.array,
  /**
   * Ref object for the trigger button
   */
  triggerRef: PropTypes.object,
  /**
   * data-qa property
   */
  qaId: PropTypes.string,
  /**
   * Toggle to close menu on item selection
   */
  closeOnSelection: PropTypes.bool
};
const defaultProps = {
  align: 'left',
  buttonStyle: 'asc-btn--secondary asc-btn--small',
  linkHOC: link => link,
  closeOnSelection: true
};
function MenuButton(_ref) {
  let {
    align,
    buttonText,
    buttonStyle,
    className,
    id,
    label,
    linkHOC,
    menuItems,
    triggerRef,
    qaId,
    closeOnSelection
  } = _ref;
  const buttonListRef = useRef();
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [closeDropdown, setCloseDropdown] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const onMenuKeyDown = e => {
    const keyCodes = {
      LEFT: 37,
      UP: 38,
      RIGHT: 39,
      DOWN: 40
    };
    if (e.keyCode >= keyCodes.LEFT && e.keyCode <= keyCodes.DOWN) {
      e.preventDefault();
      const itemCount = menuItems.length;
      let newIndex;
      if (e.keyCode === keyCodes.UP || e.keyCode === keyCodes.LEFT) {
        newIndex = (focusedIndex - 1 + itemCount) % itemCount;
      } else {
        newIndex = (focusedIndex + 1) % itemCount;
      }
      setFocusedIndex(newIndex);
      FocusService.focusFirstElementIn(buttonListRef.current, newIndex);
    }
  };
  const menuContent = /*#__PURE__*/React.createElement("ul", {
    ref: buttonListRef
  }, menuItems.map((item, i) => {
    let menuItem;
    if (item.action) {
      menuItem = /*#__PURE__*/React.createElement("button", _extends({
        onKeyDown: onMenuKeyDown,
        onClick: () => {
          item.action();
          if (closeOnSelection) {
            setCloseDropdown(true);
          }
        },
        tabIndex: "-1"
      }, item.customAttrs), item.label);
    } else {
      menuItem = linkHOC( /*#__PURE__*/React.createElement("a", _extends({
        href: item.link,
        onKeyDown: onMenuKeyDown,
        tabIndex: "-1"
      }, item.customAttrs), item.label));
    }
    return /*#__PURE__*/React.createElement("li", {
      key: i,
      className: "asc-btn-menu--item"
    }, menuItem);
  }));
  let iconColor = 'default';
  if (buttonStyle === 'asc-btn') {
    iconColor = 'white';
  } else if (buttonStyle === 'asc-btn--link' && menuIsOpen) {
    iconColor = 'brand-link-hover';
  } else if (buttonStyle === 'asc-btn--link') {
    iconColor = 'brand-link';
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cx(className, 'asc-btn-menu', `asc-btn-menu--${align}`),
    "data-qa": qaId
  }, /*#__PURE__*/React.createElement(Dropdown, {
    buttonClassName: `asc-btn asc-btn-menu--btn ${buttonStyle}`,
    buttonText: buttonText,
    content: menuContent,
    id: id,
    label: label,
    onOpen: () => {
      setFocusedIndex(0);
      setMenuIsOpen(true);
    },
    onClose: () => {
      setCloseDropdown(false);
      setMenuIsOpen(false);
    },
    triggerRef: triggerRef,
    closeWhen: closeDropdown,
    iconProps: {
      size: 'xxs',
      color: iconColor
    }
  }));
}
MenuButton.propTypes = propTypes;
MenuButton.defaultProps = defaultProps;
export default MenuButton;